import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { DeleteOutline, AddCircleOutline } from "@mui/icons-material";
import useLoader from "../../../../hooks/useLoader.js";
import useModal from "../../../../hooks/useModal.js";
import { createPtMaster, updatePtMaster, getPtMasters, getZones } from "../../../../services/ptMaster.js";

// Compliance Type is never shown to the user - this module only ever manages PT records.
const COMPLIANCE_TYPE = "PT";

const STATUS_OPTIONS = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];

const INITIAL_FORM = {
  zone_id: "",
  salary_from: "",
  salary_to: "",
  pt_amount: "",
  status: 1,
};

// Backend validation errors (zod) are keyed by the backend field name. Kept as an explicit
// map (even though it's empty today) so a future rename on the API side is a one-line fix
// here, mirroring the same pattern used in AddEditLWFMaster.jsx.
const SERVER_FIELD_MAP = {};

const isBlank = (val) => val === "" || val === null || val === undefined;

// Same closed-interval overlap test the backend uses: existing.salary_from <= newTo AND
// existing.salary_to >= newFrom. Boundary values count as overlapping (0-1200 then 1200-1500
// is rejected; 1201-1500 is not), so gaps between ranges stay allowed without forcing a
// continuous "previous salary_to + 1" sequence.
const findOverlap = (from, to, candidates) => candidates.find((c) => c.salary_from <= to && c.salary_to >= from);

// Pure validation for a single row's fields. conflictRanges is the combined set of
// existing DB ranges (for the row's zone, excluding the record itself on edit) plus every
// other row currently open in the same modal that shares that zone.
function computeFieldErrors(formData, { conflictRanges }) {
  const errors = {};

  if (!formData.zone_id) errors.zone_id = "State is required";

  const fromBlank = isBlank(formData.salary_from);
  const toBlank = isBlank(formData.salary_to);
  const fromNum = Number(formData.salary_from);
  const toNum = Number(formData.salary_to);
  const fromValid = !fromBlank && !Number.isNaN(fromNum) && fromNum >= 0;
  const toValid = !toBlank && !Number.isNaN(toNum) && toNum >= 0;

  if (fromBlank) {
    errors.salary_from = "Salary From is required";
  } else if (Number.isNaN(fromNum)) {
    errors.salary_from = "Salary From must be a valid number";
  } else if (fromNum < 0) {
    errors.salary_from = "Salary From must be greater than or equal to 0";
  }

  // Salary To is optional: a blank value means the range is open-ended (no upper bound).
  if (!toBlank) {
    if (Number.isNaN(toNum)) {
      errors.salary_to = "Salary To must be a valid number";
    } else if (toNum < 0) {
      errors.salary_to = "Salary To must be greater than or equal to 0";
    }
  }

  // Overlap check runs off Salary From alone once it's valid, using Salary To when present or
  // treating the range as open-ended (up to infinity) when it's blank, so an in-range value is
  // flagged immediately instead of waiting for both fields to be filled.
  if (fromValid && !errors.salary_from) {
    if (toValid && !errors.salary_to && toNum <= fromNum) {
      errors.salary_to = "Salary To must be greater than Salary From";
    } else if (formData.zone_id && conflictRanges && conflictRanges.length) {
      const effectiveTo = toValid && !errors.salary_to ? toNum : Infinity;
      const conflict = findOverlap(fromNum, effectiveTo, conflictRanges);
      if (conflict) {
        if (toValid && !errors.salary_to) {
          errors.salary_to = `Salary range overlaps with existing range ${conflict.salary_from}-${conflict.salary_to} for this state`;
        } else {
          errors.salary_from = `Salary From must be greater than ${conflict.salary_to} (overlaps with existing range ${conflict.salary_from}-${conflict.salary_to})`;
        }
      }
    }
  }

  if (!isBlank(formData.pt_amount)) {
    if (Number.isNaN(Number(formData.pt_amount))) {
      errors.pt_amount = "PT Amount must be a valid number";
    } else if (Number(formData.pt_amount) < 0) {
      errors.pt_amount = "PT Amount must be greater than or equal to 0";
    }
  }

  return errors;
}

function getConflictRangesForRow(rowsSnapshot, index, rangesByZone, excludeId) {
  const row = rowsSnapshot[index];
  const zoneId = row.formData.zone_id;
  if (!zoneId) return [];
  const key = String(zoneId);

  const dbRanges = (rangesByZone[key]?.ranges || [])
    .filter((r) => !excludeId || r.id !== excludeId)
    .map((r) => ({ ...r, salary_to: isBlank(r.salary_to) || Number.isNaN(r.salary_to) ? Infinity : r.salary_to }));

  const siblingRanges = rowsSnapshot
    .filter((r, i) => i !== index && String(r.formData.zone_id) === key)
    .map((r) => ({
      salary_from: Number(r.formData.salary_from),
      salary_to: isBlank(r.formData.salary_to) ? Infinity : Number(r.formData.salary_to),
    }))
    .filter((r) => !Number.isNaN(r.salary_from) && !Number.isNaN(r.salary_to));

  return [...dbRanges, ...siblingRanges];
}

function recomputeAllRows(rowsSnapshot, rangesByZone, excludeId) {
  return rowsSnapshot.map((row, index) => {
    if (row.saved) return row;
    const conflictRanges = getConflictRangesForRow(rowsSnapshot, index, rangesByZone, excludeId);
    const errors = computeFieldErrors(row.formData, { conflictRanges });
    return { ...row, errors };
  });
}

function extractServerFieldErrors(respData) {
  const issues = Array.isArray(respData?.data) ? respData.data : null;
  if (!issues || !issues.length) return {};

  const fieldErrors = {};
  issues.forEach((issue) => {
    const backendField = issue?.path?.[0];
    if (!backendField) return;
    const field = SERVER_FIELD_MAP[backendField] || backendField;
    fieldErrors[field] = issue.message;
  });
  return fieldErrors;
}

function buildPayload(formData) {
  return {
    compliance_type: COMPLIANCE_TYPE,
    zone_id: Number(formData.zone_id),
    salary_from: Number(formData.salary_from),
    salary_to: isBlank(formData.salary_to) ? null : Number(formData.salary_to),
    pt_amount: isBlank(formData.pt_amount) ? null : Number(formData.pt_amount),
    status: Number(formData.status),
  };
}

const PtRowForm = ({ row, index, zones, isEdit, canRemove, isSubmitting, onFieldChange, onRemove }) => {
  const { formData, errors, saved } = row;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "status") {
      onFieldChange(index, name, Number(value));
    } else {
      onFieldChange(index, name, type === "checkbox" ? checked : value);
    }
  };

  const disabled = saved || isSubmitting;

  return (
    <div
      style={{
        backgroundColor: "#f9f9f9",
        padding: "14px",
        borderRadius: "6px",
        border: saved ? "1px solid #28a745" : "1px solid #e0e0e0",
        marginBottom: "14px",
      }}
    >
      {!isEdit && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong style={{ fontSize: "13px" }}>Row {index + 1}</strong>
          {canRemove && !saved && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={isSubmitting}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#dc3545", padding: 0 }}
              title="Remove row"
            >
              <DeleteOutline fontSize="small" />
            </button>
          )}
        </div>
      )}

      <div className="row">
        <div className="col-md-3 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            State <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select form-select-sm ${errors.zone_id ? "is-invalid" : ""}`}
            name="zone_id"
            value={formData.zone_id}
            onChange={handleChange}
            disabled={disabled}
          >
            <option value="">Select State</option>
            {zones.map((zone) => (
              <option key={zone._ZoneID} value={zone._ZoneID}>
                {zone._Name}
              </option>
            ))}
          </select>
          {errors.zone_id && <div className="invalid-feedback">{errors.zone_id}</div>}
        </div>

        <div className="col-md-3 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Salary From <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            min="0"
            name="salary_from"
            className={`form-control form-control-sm ${errors.salary_from ? "is-invalid" : ""}`}
            value={formData.salary_from}
            onChange={handleChange}
            placeholder="Enter salary from"
            disabled={disabled}
          />
          {errors.salary_from && <div className="invalid-feedback">{errors.salary_from}</div>}
        </div>

        <div className="col-md-3 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Salary To
          </label>
          <input
            type="number"
            min="0"
            name="salary_to"
            className={`form-control form-control-sm ${errors.salary_to ? "is-invalid" : ""}`}
            value={formData.salary_to}
            onChange={handleChange}
            placeholder="Enter salary to (optional)"
            disabled={disabled}
          />
          {errors.salary_to && <div className="invalid-feedback">{errors.salary_to}</div>}
        </div>

        <div className="col-md-3 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            PT Amount
          </label>
          <input
            type="number"
            min="0"
            name="pt_amount"
            className={`form-control form-control-sm ${errors.pt_amount ? "is-invalid" : ""}`}
            value={formData.pt_amount}
            onChange={handleChange}
            placeholder="PT Amount"
            disabled={disabled}
          />
          {errors.pt_amount && <div className="invalid-feedback">{errors.pt_amount}</div>}
        </div>

        <div className="col-md-3 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Status
          </label>
          <select className="form-select form-select-sm" name="status" value={formData.status} onChange={handleChange} disabled={disabled}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!isEdit && saved && (
        <div className="d-flex justify-content-end align-items-center" style={{ paddingTop: "6px" }}>
          <span className="text-success" style={{ fontSize: "12px", fontWeight: 600 }}>
            Saved successfully
          </span>
        </div>
      )}
    </div>
  );
};

const AddEditPTMaster = ({ refresh, existingField = null }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();
  const isEdit = Boolean(existingField);
  const excludeId = isEdit ? existingField.id : undefined;

  const [zones, setZones] = useState([]);
  const [rangesByZone, setRangesByZone] = useState({});
  const rangesByZoneRef = useRef({});

  const rowCounterRef = useRef(0);
  const makeEmptyRow = () => ({
    key: ++rowCounterRef.current,
    formData: { ...INITIAL_FORM },
    errors: {},
    saved: false,
  });

  const [rows, setRows] = useState(() => (isEdit ? [] : [makeEmptyRow()]));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Errors stay hidden while the user is filling the form in and only appear once they've
  // clicked Submit/Update at least once; after that first attempt, edits re-validate live so
  // fixed fields clear immediately instead of requiring another submit click.
  const attemptedSubmitRef = useRef(false);
  const applyRows = useCallback(
    (nextRows) => (attemptedSubmitRef.current ? recomputeAllRows(nextRows, rangesByZoneRef.current, excludeId) : nextRows),
    [excludeId]
  );

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const response = await getZones();
        const zoneData = response?.data?.data || response?.data || [];
        setZones(zoneData);
      } catch (error) {
        console.error("Error fetching states:", error);
        toast.error("Failed to load states");
      }
    };
    fetchZones();
  }, []);

  // Mirrors ensureRangesLoaded in AddEditLWFMaster.jsx: caches existing PT ranges per zone
  // so overlap validation doesn't refetch on every keystroke, and can be force-refreshed
  // right after a successful save so the next new row sees the just-created range too.
  const ensureRangesLoaded = useCallback(async (zoneId, { force = false } = {}) => {
    if (!zoneId) return;
    const key = String(zoneId);
    const current = rangesByZoneRef.current[key];
    if (!force && current && (current.loading || current.loaded)) return;

    const loadingState = { ...rangesByZoneRef.current, [key]: { loading: true, loaded: false, ranges: current?.ranges || [] } };
    rangesByZoneRef.current = loadingState;
    setRangesByZone(loadingState);

    try {
      const response = await getPtMasters(1, { filters: { zone_id: zoneId, status: 1 }, limit: 1000 });
      const body = response?.data;
      const listRows = body?.data?.data || body?.data || [];
      const ranges = listRows.map((r) => ({
        id: r.id,
        salary_from: Number(r.salary_from),
        salary_to: isBlank(r.salary_to) ? Infinity : Number(r.salary_to),
      }));

      const nextState = { ...rangesByZoneRef.current, [key]: { loading: false, loaded: true, ranges } };
      rangesByZoneRef.current = nextState;
      setRangesByZone(nextState);
      if (attemptedSubmitRef.current) {
        setRows((prev) => recomputeAllRows(prev, nextState, excludeId));
      }
    } catch (error) {
      console.error("Error fetching existing PT ranges:", error);
      const nextState = { ...rangesByZoneRef.current, [key]: { loading: false, loaded: true, ranges: current?.ranges || [] } };
      rangesByZoneRef.current = nextState;
      setRangesByZone(nextState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeId]);

  useEffect(() => {
    if (isEdit && existingField) {
      const formData = {
        zone_id: existingField.zone_id || existingField.zoneId || existingField._ZoneID || "",
        salary_from: existingField.salary_from ?? "",
        salary_to: existingField.salary_to ?? "",
        pt_amount: existingField.pt_amount ?? existingField.employee_contribution ?? "",
        status: existingField.status === undefined || existingField.status === null ? 1 : Number(existingField.status),
      };
      setRows([{ key: 1, formData, errors: {}, saved: false }]);
      if (formData.zone_id) {
        ensureRangesLoaded(formData.zone_id, { force: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existingField]);

  const handleFieldChange = (index, name, value) => {
    setRows((prev) => {
      const next = prev.map((row, i) => (i === index ? { ...row, formData: { ...row.formData, [name]: value } } : row));
      return applyRows(next);
    });

    if (name === "zone_id" && value) {
      ensureRangesLoaded(value);
    }
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => applyRows(prev.filter((_, i) => i !== index)));
  };

  const handleSubmitAll = async () => {
    if (isSubmitting) return;

    attemptedSubmitRef.current = true;
    const validatedRows = recomputeAllRows(rows, rangesByZoneRef.current, excludeId);
    const hasErrors = validatedRows.some((row) => !row.saved && Object.keys(row.errors).length > 0);

    if (hasErrors) {
      setRows(validatedRows);
      return;
    }

    setRows(validatedRows);
    setIsSubmitting(true);
    showLoader();

    try {
      let latestRows = validatedRows;
      let successMessage = "";
      let sawFailure = false;

      for (let index = 0; index < latestRows.length; index += 1) {
        const row = latestRows[index];
        if (row.saved) continue;

        const payload = buildPayload(row.formData);

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = isEdit ? await updatePtMaster(existingField.id, payload) : await createPtMaster(payload);

          successMessage = response?.data?.message || successMessage;
          latestRows = latestRows.map((r, i) => (i === index ? { ...r, saved: true, errors: {} } : r));
          setRows(latestRows);

          if (!isEdit) {
            ensureRangesLoaded(row.formData.zone_id, { force: true });
          }
        } catch (error) {
          console.error("Error saving PT Master:", error);
          sawFailure = true;
          const respData = error?.response?.data;
          const serverFieldErrors = extractServerFieldErrors(respData);
          const message = respData?.message || "Failed to save PT Master";
          const hasFieldErrors = Object.keys(serverFieldErrors).length > 0;

          latestRows = latestRows.map((r, i) => (i === index ? { ...r, errors: { ...r.errors, ...serverFieldErrors } } : r));
          setRows(latestRows);
          if (!hasFieldErrors) {
            // Business/overlap validation errors (e.g. salary range overlap) have no single
            // field to attach to, so they're surfaced via toast only - never rendered inline.
            toast.error(message);
          }
        }
      }

      if (!sawFailure) {
        toast.success(successMessage || (isEdit ? "PT Master updated successfully!" : "PT Master added successfully!"));
        refresh?.();
        hideModal?.();
      }
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "4px" }}>
        {rows.map((row, index) => (
          <PtRowForm
            key={row.key}
            row={row}
            index={index}
            zones={zones}
            isEdit={isEdit}
            canRemove={rows.length > 1}
            isSubmitting={isSubmitting}
            onFieldChange={handleFieldChange}
            onRemove={handleRemoveRow}
          />
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "12px",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <div>
          {!isEdit && (
            <button
              type="button"
              onClick={handleAddRow}
              disabled={isSubmitting}
              style={{
                backgroundColor: "#f0f0f0",
                border: "1px dashed #999",
                padding: "5px 10px",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "#333",
              }}
            >
              <AddCircleOutline fontSize="small" />
              Add Row
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isSubmitting}>
            {isEdit ? "Cancel" : "Close"}
          </button>
          <button type="button" className="btn btn-success btn-sm" disabled={isSubmitting} onClick={handleSubmitAll}>
            {isSubmitting ? "Saving..." : isEdit ? "Update" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditPTMaster;